import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  OPEN_CONSENT_EVENT,
  acceptAll,
  getConsent,
  hasAnswered,
  rejectAll,
  setConsent,
} from "../lib/consent";

/**
 * First-visit cookie banner + preferences panel.
 * Nestly state machine / visibility / markup — Glowkit ink + pink tokens only.
 * No portal: fixed bottom like Nestly (createPortal was a Glowkit-only divergence).
 */
export default function CookieBanner() {
  const titleId = useId();
  const panelTitleId = useId();
  // Eager first paint — do not wait on useEffect (matches Nestly CMP).
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return !hasAnswered();
  });
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const dialogRef = useRef(null);

  const syncFromStored = useCallback(() => {
    const stored = getConsent();
    if (stored) {
      setAnalytics(stored.analytics);
      setMarketing(stored.marketing);
    } else {
      setAnalytics(false);
      setMarketing(false);
    }
  }, []);

  useEffect(() => {
    if (!hasAnswered()) {
      setVisible(true);
    }
    const onOpen = () => {
      syncFromStored();
      setPrefsOpen(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_CONSENT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, onOpen);
  }, [syncFromStored]);

  useEffect(() => {
    if (!visible || !dialogRef.current) return undefined;
    const node = dialogRef.current;
    const focusSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const getFocusable = () =>
      Array.from(node.querySelectorAll(focusSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true",
      );

    getFocusable()[0]?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (prefsOpen && hasAnswered()) {
          setPrefsOpen(false);
          setVisible(false);
        }
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [visible, prefsOpen]);

  if (!visible) return null;

  const closeAfterChoice = () => {
    setPrefsOpen(false);
    setVisible(false);
  };

  const onAccept = () => {
    acceptAll();
    closeAfterChoice();
  };

  const onReject = () => {
    rejectAll();
    closeAfterChoice();
  };

  const onSavePrefs = () => {
    setConsent({ analytics, marketing });
    closeAfterChoice();
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[200] p-4 md:p-6 pointer-events-none"
      data-testid="cookie-banner"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={prefsOpen ? panelTitleId : titleId}
        className="pointer-events-auto mx-auto max-w-3xl border-2 border-pink bg-ink text-white shadow-[0_-8px_40px_rgba(11,12,16,0.35)]"
      >
        {!prefsOpen ? (
          <div className="p-6 md:p-8">
            <p
              id={titleId}
              className="font-serif text-2xl md:text-3xl text-white leading-tight"
            >
              Cookie e preferenze
            </p>
            <p className="mt-3 text-sm md:text-base text-white/80 leading-relaxed">
              Usiamo solo cookie tecnici necessari al funzionamento del sito. Eventuali
              strumenti analitici o di marketing (se attivati in futuro) restano spenti
              finché non scegli.{" "}
              <Link to="/cookie" className="underline underline-offset-2 text-pink">
                Cookie policy
              </Link>
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={onAccept}
                className="flex-1 min-h-11 px-5 py-3 text-sm uppercase tracking-[0.16em] font-medium bg-white text-ink hover:bg-pink transition-colors"
                data-testid="cookie-accept"
              >
                Accetta
              </button>
              <button
                type="button"
                onClick={onReject}
                className="flex-1 min-h-11 px-5 py-3 text-sm uppercase tracking-[0.16em] font-medium border border-white/40 text-white hover:border-pink hover:text-pink transition-colors"
                data-testid="cookie-reject"
              >
                Rifiuta
              </button>
              <button
                type="button"
                onClick={() => {
                  syncFromStored();
                  setPrefsOpen(true);
                }}
                className="flex-1 min-h-11 px-5 py-3 text-sm uppercase tracking-[0.16em] font-medium text-pink hover:underline"
                data-testid="cookie-customize"
              >
                Personalizza
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 md:p-8">
            <p
              id={panelTitleId}
              className="font-serif text-2xl md:text-3xl text-white leading-tight"
            >
              Preferenze cookie
            </p>
            <p className="mt-3 text-sm text-white/80 leading-relaxed">
              I cookie tecnici sono sempre attivi. Analytics e marketing restano off di
              default finché non li attivi.{" "}
              <Link to="/cookie" className="underline underline-offset-2 text-pink">
                Dettagli
              </Link>
            </p>

            <ul className="mt-6 space-y-4">
              <li className="flex items-start justify-between gap-4 border-b border-white/15 pb-4">
                <div>
                  <p className="text-sm font-medium text-white">Necessari</p>
                  <p className="text-xs text-white/60 mt-1 leading-relaxed">
                    Preferenze di consenso e funzionamento di base. Sempre attivi.
                  </p>
                </div>
                <span className="text-xs uppercase tracking-[0.15em] text-white/60 shrink-0 pt-1">
                  Sempre on
                </span>
              </li>
              <li className="flex items-start justify-between gap-4 border-b border-white/15 pb-4">
                <div>
                  <p className="text-sm font-medium text-white">Analytics</p>
                  <p className="text-xs text-white/60 mt-1 leading-relaxed">
                    Statistiche di utilizzo aggregate, solo se configurate e con consenso.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer shrink-0 pt-1">
                  <span className="sr-only">Attiva analytics</span>
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="h-5 w-5 accent-pink"
                    data-testid="cookie-toggle-analytics"
                  />
                </label>
              </li>
              <li className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-white">Marketing</p>
                  <p className="text-xs text-white/60 mt-1 leading-relaxed">
                    Pixel o profilazione di marketing. Al momento non attivi sul sito.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer shrink-0 pt-1">
                  <span className="sr-only">Attiva marketing</span>
                  <input
                    type="checkbox"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    className="h-5 w-5 accent-pink"
                    data-testid="cookie-toggle-marketing"
                  />
                </label>
              </li>
            </ul>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={onSavePrefs}
                className="flex-1 min-h-11 px-5 py-3 text-sm uppercase tracking-[0.16em] font-medium bg-white text-ink hover:bg-pink transition-colors"
                data-testid="cookie-save-prefs"
              >
                Salva preferenze
              </button>
              <button
                type="button"
                onClick={onReject}
                className="flex-1 min-h-11 px-5 py-3 text-sm uppercase tracking-[0.16em] font-medium border border-white/40 text-white hover:border-pink hover:text-pink transition-colors"
              >
                Rifiuta
              </button>
              {!hasAnswered() ? (
                <button
                  type="button"
                  onClick={() => setPrefsOpen(false)}
                  className="flex-1 min-h-11 px-5 py-3 text-sm uppercase tracking-[0.16em] font-medium text-pink hover:underline"
                >
                  Indietro
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
